import React, { Component, useContext } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { Theme } from '@material-ui/core';
import { makeStyles, createStyles } from '@material-ui/core/styles';
import { Typography } from './Typography';
import { Menu, MenuItem, SubMenu, menuClasses, MenuItemStyles } from 'react-pro-sidebar';
import { Badge } from './Badge';
import { categories, Category } from './SideBarCategories';
import { NavigationContext } from '../../../../../context/NavigationContext';
import styled from '@emotion/styled';
import { textDecorationLine } from 'html2canvas/dist/types/css/property-descriptors/text-decoration-line';


// Define categories with types


const themes = {
  light: {
    sidebar: {
      backgroundColor: '#ffffff',
      color: '#000000',
    },
    menu: {
      menuContent: '#fbfcfd',
      icon: '#0098e5',
      hover: {
        backgroundColor: '#c5e4ff',
        color: '#44596e',
      },
      disabled: {
        color: '#9fb6cf',
      },
    },
  },
  dark: {
    sidebar: {
      backgroundColor: '#0b2948',
      color: '#8ba1b7',
    },
    menu: {
      menuContent: '#082440',
      icon: '#59d0ff',
      hover: {
        backgroundColor: '#00458b',
        color: '#b6c8d9',
      },
      disabled: {
        color: '#3e5e7e',
      },
    },
  },
};

// hex to rgba converter
const hexToRgba = (hex: string, alpha: number) => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);

  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

// Utilize makeStyles for styling
const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    sidebarSection: {
      margin: theme.spacing(2, 0),
    },
    listItem: {
      paddingLeft: theme.spacing(4),
    },
    stickyCategory: {
      marginTop: 'auto',
    },
  }),
);

interface SidebarContentProps extends React.HTMLAttributes<HTMLDivElement> {
  collapsed?: boolean;
  hasImage?: boolean;
}
export const SidebarContent: React.FC<SidebarContentProps> = ({ collapsed, hasImage, ...rest }) => {
  const categoryList = useContext(NavigationContext);

  const menuItemStyles: MenuItemStyles = {
    root: {
      fontSize: '13px',
      fontWeight: 400,
    },
    icon: {
      fontSize: '20px',
      margin: 0,
      transform: 'translateY(-2px)',
      [`&.${menuClasses.disabled}`]: {
        color: themes['light'].menu.disabled.color,
      },
    },
    SubMenuExpandIcon: {
      color: '#b6b7b9',
    },
    subMenuContent: ({ level }) => ({
      backgroundColor:
        level === 0
          ? hexToRgba(themes['light'].menu.menuContent, hasImage && !collapsed ? 0.4 : 1)
          : 'transparent',
    }),
    button: {
      [`&.${menuClasses.disabled}`]: {
        color: themes['light'].menu.disabled.color,
      },
      '&:hover': {
        backgroundColor: hexToRgba(themes['light'].menu.hover.backgroundColor, hasImage ? 0.8 : 1),
        color: themes['light'].menu.hover.color,
      },
    },
    label: ({ open }) => ({
      fontWeight: open ? 600 : undefined,
    }),
  };

  const renderCategory = (category: Category, level: number = 0, index = 0, basePath = ''): JSX.Element => {
    const linkStyle = { color: 'inherit', textDecoration: 'none' };

    if (category.title) {
      return (
        <div key={level.toString() + '_' + index.toString() + '_' + category.name}
             style={{ padding: '0 24px', marginBottom: '8px', marginTop: '32px' }}>
          <Typography
            variant='body2'
            fontWeight={600}
            style={{ opacity: collapsed ? 0 : 0.7, letterSpacing: '0.5px' }}
          >
            {(!collapsed || index>0) && category.name}
          </Typography>
        </div>
      );
    }

    let fullpath='';
    let queryParams = '';

    if (category.path) {
        // Handle scenarios where a direct path is provided
        fullpath = `${basePath}/${category.path}`.replace(/\/+/g, '/');
    } else if (category.params) {
        // Construct a query string if params are provided instead of a path
        const queryParamStrings = Object.entries(category.params).map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`);
        queryParams = `?${queryParamStrings.join('&')}`;
        fullpath = basePath; // Use the base path; append query params later
    }


    if (!category.name) {
      return (
        <Menu key={level.toString() + '_' + index.toString() + '_' + category.name} menuItemStyles={menuItemStyles}>
          {category.children && category.children.map((child, childIndex) => renderCategory(child, level + 1, childIndex, fullpath))}
        </Menu>
      );
    }

    if (!category.children) {
      return (

        <MenuItem key={level.toString() + '_' + index.toString() + '_' + category.name}
                  component={React.createElement(Link, { to: `${fullpath}${queryParams}` }, category.name)}

                  icon={category.icon}
                  suffix={category.badgeNumber !== undefined &&
                  <Badge variant='danger' shape='circle'>
                      {Number(category.badgeNumber)}
                    </Badge>
                  }

        > {(!collapsed || level>1) && category.name}</MenuItem>

      );
    }

    return (
      <SubMenu defaultOpen={true} key={level.toString() + '_' + index.toString() + '_' + category.name}
               label={(!collapsed || level>1) && category.name}
               icon={category.icon}
               suffix={category.badge && category.badgeNumber !== undefined &&
                 <Badge variant='danger' shape='circle'>
                    {Number(category.badgeNumber)}
                 </Badge>
               }

      >
        {category.children && category.children.map((child, childIndex) => renderCategory(child, level + 1, childIndex, fullpath))}
      </SubMenu>
    );
  };

  return (

    <div style={{ flex: 1, marginBottom: '32px' }}>

      {categoryList && categoryList.filter(c => !c.sticky).map((category, index) => renderCategory(category, 0, index))}


    </div>

  );
};

